import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ISDialogStyleHandlerService, ISDialogTaskbarItem } from './is-dialog-style-handler.service';
import { ISDialogLayout } from './is-dialog-workspace-layout';

@Component({
  selector: 'is-dialog-taskbar',
  templateUrl: './is-dialog-taskbar.component.html',
  styleUrls: ['./is-dialog-taskbar.component.scss']
})
export class ISDialogTaskbarComponent implements OnInit, OnDestroy {
  all: ISDialogTaskbarItem[] = [];
  minimized: ISDialogTaskbarItem[] = [];
  maxVisible = 4;
  managerOpen = false;
  query = '';
  filter: 'all' | 'open' | 'min' = 'all';
  settingsOpen = false;
  taskbarPosition: 'left' | 'center' | 'right' = 'center';
  private subscription?: Subscription;
  private readonly positionKey = 'isdialog:taskbar-position:v3';

  constructor(public handler: ISDialogStyleHandlerService, private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem(this.positionKey);
      if (saved === 'left' || saved === 'center' || saved === 'right') this.taskbarPosition = saved;
    } catch { /* Armazenamento pode estar desativado. */ }
    this.updateCapacity();
    this.subscription = this.handler.windows$.subscribe(items => {
      this.all = items;
      this.minimized = items.filter(item => item.minimized);
    });
  }

  ngOnDestroy(): void { this.subscription?.unsubscribe(); }

  @HostListener('window:resize')
  updateCapacity(): void {
    this.maxVisible = Math.max(1, Math.min(7, Math.floor((window.innerWidth - 165) / 195)));
  }

  @HostListener('document:pointerdown', ['$event'])
  outsidePointer(event: PointerEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.managerOpen = false;
      this.settingsOpen = false;
    }
  }

  @HostListener('window:keydown', ['$event'])
  onShortcut(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.managerOpen) {
      this.managerOpen = false;
      event.preventDefault();
      return;
    }
    if (!event.ctrlKey || !event.altKey || !event.shiftKey || event.metaKey || event.repeat) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')) return;
    const key = event.key.toLowerCase();
    if (key === 'j') {
      event.preventDefault();
      this.managerOpen = !this.managerOpen;
    } else if (key === 'k') {
      event.preventDefault();
      this.handler.focusNextWindow();
    } else if (key === 'm') {
      event.preventDefault();
      this.handler.activeWindow?.minimize();
    } else if (key === 'arrowleft' || key === 'arrowright') {
      const active = this.handler.activeWindow;
      if (active && !active.inPictureInPicture) {
        event.preventDefault();
        active.snap?.(key === 'arrowleft' ? 'left' : 'right');
      }
    }
  }

  get visible(): ISDialogTaskbarItem[] { return this.minimized.slice(0, this.maxVisible); }
  get overflow(): ISDialogTaskbarItem[] { return this.minimized.slice(this.maxVisible); }
  get results(): ISDialogTaskbarItem[] {
    const term = this.query.trim().toLocaleLowerCase();
    return this.all.filter(item =>
      (this.filter === 'all' || (this.filter === 'min' ? item.minimized : !item.minimized)) &&
      (!term || item.title.toLocaleLowerCase().includes(term))
    );
  }
  trackById(_: number, item: ISDialogTaskbarItem): string { return item.id; }

  restore(item: ISDialogTaskbarItem, event?: Event): void {
    event?.stopPropagation();
    if (item.minimized) item.restore();
    else if (item.inPictureInPicture) item.focus?.();
    else this.handler.focusWindow(item.id);
    this.managerOpen = false;
  }

  close(item: ISDialogTaskbarItem, event: Event): void {
    event.stopPropagation();
    item.close();
  }

  toggleManager(event?: Event): void {
    event?.stopPropagation();
    this.managerOpen = !this.managerOpen;
    this.settingsOpen = false;
  }

  arrange(layout: ISDialogLayout): void { this.handler.arrangeWindows(layout); this.managerOpen = false; }

  setPosition(position: 'left' | 'center' | 'right'): void {
    this.taskbarPosition = position;
    try { localStorage.setItem(this.positionKey, position); } catch { /* opcional */ }
    this.settingsOpen = false;
  }
}
